import { reactive, watch } from 'vue';

class EventDrivenStore {
  constructor(id, initialState = {}, options = {}) {
    this.id = id;
    this.initialState = { ...initialState };
    this.useLocalStorage = options.useLocalStorage || false;

    const persistedState = this.loadPersistedState();
    this.state = reactive(persistedState || { ...initialState });

    this.setupEventsIfExists();

    if (this.useLocalStorage) {
      this.startPersistenceWatcher();
    }
  }

  setupEventsIfExists() {
    if (typeof this.setupEvents === 'function') {
      this.setupEvents.call(this);
    }
  }

  get bus() {
    if (window.$bus) return window.$bus;
    if (window.__VUE_APP__?.config?.globalProperties?.$bus) {
      return window.__VUE_APP__.config.globalProperties.$bus;
    }
    return null;
  }

  emit(event, data) {
    const eventBus = this.bus;
    if (!eventBus) {
      console.warn(`[Store:${this.id}] bus not ready yet, cannot emit`, event);
      return;
    }
    this.emitToBus(eventBus, event, data);
  }

  emitToBus(eventBus, event, data) {
    eventBus.emit(event, data);
  }

  on(event, handler) {
    if (this.bus) {
      this.subscribeToBus(event, handler);
    } else {
      this.retrySubscribe(event, handler);
    }
  }

  subscribeToBus(event, handler) {
    const eventBus = this.bus;
    if (eventBus) {
      eventBus.on(event, handler);
      console.log(`[Store:${this.id}] subscribed to ${event}`);
    }
  }

  retrySubscribe(event, handler) {
    const trySubscribe = () => {
      if (this.bus) {
        this.subscribeToBus(event, handler);
      } else {
        setTimeout(trySubscribe, 10);
      }
    };
    trySubscribe();
  }

  updateState(partial) {
    Object.assign(this.state, partial);
  }

  resetState() {
    const stateToRestore = this.useLocalStorage
      ? this.loadPersistedState() || this.initialState
      : this.initialState;
    Object.assign(this.state, stateToRestore);
  }

  destroy() {
    if (!this.useLocalStorage) {
      Object.assign(this.state, this.initialState);
    }
  }

  startPersistenceWatcher() {
    const persistenceCallback = (newState) => {
      this.saveToLocalStorage(newState);
    };
    watch(this.state, persistenceCallback, { deep: true });
  }

  saveToLocalStorage(state) {
    try {
      localStorage.setItem(this.storageKey(), JSON.stringify(state));
    } catch (error) {
      console.warn(`[Store:${this.id}] failed to save to localStorage`, error);
    }
  }

  loadPersistedState() {
    if (!this.useLocalStorage) return null;
    try {
      const saved = localStorage.getItem(this.storageKey());
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn(
        `[Store:${this.id}] failed to load from localStorage`,
        error
      );
      return null;
    }
  }

  storageKey() {
    return `store:${this.id}`;
  }
}

export { EventDrivenStore };
